// src/api/accountApi.js
// Wraps the Spring backend's /account endpoint (com.experience.account.AccountController).
// Every endpoint derives the caller's uid from the auth token server-side, so
// none of these need a userId in the request body - the backend ignores it if sent.
import axiosClient from "./axiosClient";

// Jackson serializes the `isLead` boolean getter as "lead" (standard Java bean
// naming), so every account response gets remapped to isLead here.
const normalizeAccount = (data) => {
  if (!data) return data;
  const { lead, ...rest } = data;
  return { ...rest, isLead: lead === true };
};

// Returns the account, or null if it doesn't exist yet (backend returns 404).
export const getAccount = async () => {
  try {
    const response = await axiosClient.get("/account");
    return normalizeAccount(response.data);
  } catch (error) {
    if (error?.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

// Idempotent: the backend returns the existing account if one already exists.
export const createAccount = async ({ email }) => {
  const response = await axiosClient.post("/account", { email });
  return normalizeAccount(response.data);
};

export const setAccountOrganization = async ({ organizationId }) => {
  const response = await axiosClient.put("/account", { organizationId });
  return normalizeAccount(response.data);
};

// Ensures the account exists and stamps last_login_at. Call once per sign-in.
export const recordLogin = async () => {
  const response = await axiosClient.post("/account/login");
  return normalizeAccount(response.data);
};

// Only provided fields are updated; omit a field to leave it untouched.
export const updateAccountProfile = async ({ defaultLanguage, isLead } = {}) => {
  const response = await axiosClient.put("/account/profile", {
    defaultLanguage,
    isLead,
  });
  return normalizeAccount(response.data);
};
